import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocalTimer } from '../../hooks/useLocalTimer';
import { TimerContinueModal } from '../timer/TimerContinueModal';

// Props interface for the TimerCheckWrapper component
// children: The app content to wrap with timer checking functionality
interface TimerCheckWrapperProps {
  children: React.ReactNode;
}

/**
 * TimerCheckWrapper Component
 * 
 * This component acts as a traffic controller for when users have a timer running.
 * If a user logs in or navigates around the app while a task timer is running,
 * this component automatically redirects them to the Focus Mode page.
 * 
 * Think of it like a helpful assistant that says "Hey, you left a timer running!
 * Let me take you back to where you were working."
 * 
 * How it works:
 * 1. Checks if there's a timer currently running
 * 2. If timer is running and user is not on Focus Mode page, redirect them there
 * 3. Only does this check once when the component first loads
 * 4. Also provides a modal for continuing timer work (currently unused)
 * 
 * @param children - The app content to display after timer checks
 */
export function TimerCheckWrapper({ children }: TimerCheckWrapperProps) {
  // Get timer status: is a timer currently running and which task?
  const { isRunning, currentTaskId } = useLocalTimer();
  
  // Navigation functions to move between pages
  const navigate = useNavigate();
  const location = useLocation();
  
  // Modal state (currently not used, but available for future timer continuation UI)
  const [showModal, setShowModal] = useState(false);
  
  // Flag to ensure we only check the timer status once when component loads
  const [hasChecked, setHasChecked] = useState(false);

  // Effect that runs when component loads or timer status changes
  useEffect(() => {
    // Only check once on mount to avoid constant redirects
    if (!hasChecked && isRunning && currentTaskId) {
      // Mark that we've done our check
      setHasChecked(true);
      
      // If user has a timer running but they're not on the Focus Mode page,
      // automatically redirect them there so they can see their timer
      if (location.pathname !== '/focus') {
        navigate('/focus', { replace: true });
      }
    }
  }, [isRunning, currentTaskId, hasChecked, navigate, location.pathname]);

  return (
    <>
      {/* Show the main app content */}
      {children}
      {/* Timer continuation modal (currently unused but available) */}
      <TimerContinueModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
      />
    </>
  );
}